(function (global, factory) {
  if (typeof define === 'function' && define.amd) {
    // If supports for AMD is available return the value to the AMD Loader
    // and to the global (window) object in case any JS module is expecting
    // the AppNexusModule to exists on the window object.
    define([], () => (global.AppNexus = factory(global)));
  } else if (typeof exports === 'object') {
    // If supports for Common JS is available export the module
    // and pass is value to the global (window) object in case non Common JS code
    // is expected to work with this AppNexusModule.
    module.exports = global.AppNexus = factory(global);
  } else {
    // If not support for AMD or Common JS is available register the module in the global scope.
    global.AppNexus = factory(global);
  }
})(typeof window !== 'undefined' ? window : {}, (root) => {
  /* In order to avoid AppNexus running twice or more. Flag is set to true once AppNexus ran. */
  if (root.apntag) {
    return;
  }

  const baseElementUrl = document.querySelector('base')?.attributes?.href?.value;
  const baseUrl = baseElementUrl || window.location.origin;
  /**
   *
   * Using module pattern to avoid unintended modifications to the constants values needed during the execution of the script. ECMAScript5 limitation not const word exists.
   *
   * @constant
   */
  const CONFIGURATION = (function () {
    //
    const constants = {
      ASTURL: '//acdn.adnxs.com/ast/ast.js', // URL were the AST JS resource is located.
      PARAMETERSURL: `${baseUrl}/GetAdParameters?url=`, // URL of the AJAX call to get the parameters.
      HTMLDIVPREFIX: 'nhs_', // Prefix shared by the ID propert of all DIV elements where we are expecting an ad.
      MEMBERID: 3232,
      SKINADID: 'nhs_Sponsorship',
    };

    const retrieveValue = function (constantName) {
      return constants[constantName];
    };

    return {
      get: retrieveValue, // Expose a get method to return the value of the constants that have being defined in this module.
    };
  })();

  /**
   * @var Empty apntag.
   */
  let apntag;

  /**
   * @var Empty parameter object to stored the backend values passed as keywords to AppNexus.
   */
  let parameters;

  /**
   * @var Flag to indicate if the AST JS Library has already being loaded. If the AST Library has not being loaded value will be 'false', otherwise 'true'.
   */
  let isAstLoaded = false;

  /**
   * @var Flag to indicate if the request to load the AST JS Library has been started. If the AST Library has is being loaded value will be 'false', otherwise 'true'.
   */
  let isAstLoading = false;

  /**
   * @var Flag to indicate if the AJAX request to load the parameters to being sent to AppNexus has being retrived from the server. If the parameters has been loaded value will be 'true', otherwise 'false'.
   */
  let areParametersDefined = false;

  /**
   * @event Custom event to indicate a new call for Ads at AppNexus must be executed.
   */
  const loadAdsEvent = document.createEvent('Event');

  /**
   * @event Custom event to indicate the AST JS library has been loaded.
   */
  const atsLoadedEvent = document.createEvent('Event');

  /**
   * @type {Function[]} List of functions to run after the Parameters get loaded
   */
  let onParametersLoaded = [];

  /**
   * @type {object} to store in memory the native ads templates.
   */
  const nativeAdTemplates = {};

  function getNodeIdsFromSelectors(selectors) {
    const adsIds = selectors.reduce((accumulator, selector) => {
      const elements = document.querySelectorAll(selector);
      return accumulator.concat(getIdsFromElements(elements));
    }, []);

    return adsIds;
  }

  /**
   * Gets all the UT calls group the page should run.
   *
   * @returns an array of CSS selectors. The array contains selectors that will be used to group ads in UT calls
   */
  const getUtCallGroups = () => {
    // Get all elements with the "data-native-ad-ut-call" attribute
    const elements = document.querySelectorAll('[data-native-ad-ut-call]:not([data-loaded])');

    // Create an empty Set to store unique attribute values
    const values = new Set();

    // Iterate through the NodeList and add each unique value to the Set
    elements.forEach((element) => {
      const value = parseInt(element.getAttribute('data-native-ad-ut-call'));

      if (!isNaN(value)) {
        values.add(value);
      }
    });

    const uniqueValues = Array.from(values).sort((a, b) => a - b);
    const groupsAsQuerySelector = uniqueValues.map((value) => `[data-native-ad-ut-call="${value}"]:not([data-loaded])`);

    groupsAsQuerySelector.push('.nhs_ASTAboveDiv');
    groupsAsQuerySelector.push('.nhs_ASTBelowDiv');

    // Convert the Set to an array and log the result
    return groupsAsQuerySelector;
  };

  /**
   *
   * Executes an XML Http Request for a given URL.
   *
   * @param {string} url URL where the file being requested lives.
   */
  function requestHttpGet(url) {
    const request = new XMLHttpRequest();
    request.open('GET', url);
    request.send();
  }

  /**
   * Replaces the content of a template with the response data.
   * @param {HTMLElement} template - The template element to use for the content.
   * @param {Object} response - The response data to use for populating the template.
   * @returns {HTMLElement} - The copy of the template with the response data.
   */
  function replaceTemplateContent(template, response) {
    const clone = template.querySelector('div').cloneNode(true);

    clone.querySelector('[data-native-image]').src = response.image.url;
    clone.querySelector('[data-native-image]').alt =  `${response.title} - ${response.sponsoredBy}`;
    clone.querySelector('[data-native-contact]').href = response.clickUrl;
    clone.querySelector('[data-native-contact]').textContent = `Sponsored by ${response.sponsoredBy}`;

    clone.querySelector('[data-native-head]').textContent = response.title;
    clone.querySelector('[data-native-description]').textContent = response.body;
    clone.querySelector('[data-native-builder-logo]').src = response.icon.url;
    clone.querySelector('[data-native-builder-logo]').alt = response.sponsoredBy;
    clone.querySelector('[data-native-cta]').textContent = response.cta;
    clone.removeAttribute('hidden');

    return clone;
  }

  /**
   *
   * Method to insert a Native Ad into a page.
   *
   * @param {string} element HTML Element where the Native Ad is going to be added.
   * @param {object} response AppNexus response with the Native Ad information.
   */
  function createNativeAd(element, response) {
    if (!response || !response.native) {
      return;
    }
    const nativeAdKey = response.targetId.split("--")[1];
    const template = (
      nativeAdTemplates[nativeAdKey] ||
      document.querySelector(`[data-native-ad-template="${response.width}x${response.height}"]`)
    ).content.cloneNode(true);
    const regex = /(?:<script.+?)(?:src=")(.+?)".*?><\/script>/g;

    if (element) {
      const impressionTrackers = response.native.impressionTrackers || [];
      const clickTrackers = response.native.clickTrackers || [];
      const jsTrackers = [];
      let result;

      const iframeForNativeAd = document.createElement('iframe');
      iframeForNativeAd.setAttribute('aria-label', 'Sponsored');

      const content = replaceTemplateContent(template, response.native);

      iframeForNativeAd.onload = function () {
        var cssForIframe = document.querySelector('[data-native-ad-styles]').value;

        const fontLink = document.createElement('link');
        fontLink.setAttribute('href', 'https://fonts.googleapis.com/css?family=Figtree&display=swap');
        fontLink.setAttribute('rel', 'stylesheet');

        var iframeStyles = document.createElement('link');
        iframeStyles.setAttribute('href', cssForIframe);
        iframeStyles.setAttribute('rel', 'stylesheet');

        // impression trackers
        let i;
        for (i = 0; i < impressionTrackers.length; i += 1) {
          const image = new Image();
          image.src = impressionTrackers[i];
          image.style.display = 'none';
          iframeForNativeAd.contentWindow.document.body.appendChild(image);
        }

        // JS trackers
        while ((result = regex.exec(response.native.javascriptTrackers)) !== null) {
          jsTrackers.push(result[1]);
        }

        for (i = 0; i < jsTrackers.length; i += 1) {
          const scr = document.createElement('script');

          scr.type = 'text/javascript';
          scr.async = true;
          scr.src = jsTrackers[i];
          iframeForNativeAd.contentWindow.document.head.appendChild(scr);
        }

        iframeForNativeAd.contentWindow.document.head.appendChild(iframeStyles);
        iframeForNativeAd.contentWindow.document.head.appendChild(fontLink);
        iframeForNativeAd.contentWindow.document.body.appendChild(content);

        bindEventsToNativeAdCard(iframeForNativeAd, element, clickTrackers);
      };

      element.appendChild(iframeForNativeAd);
      element.classList.add('loaded');
      element.removeAttribute('hidden');
      element.dataset.loaded = "true";
    }
  }

  /**
   *
   * Method to clone an orignal object except for one property.
   *
   * @private
   * @param {object} original Object to be cloned.
   * @param {string[]} undesiredKeys name of the keys we do not want to exist on the cloned object.
   * @returns {object} a clone version of the original object with all properties except one.
   */
  const _omit = function (original, undesiredKeys) {
    const clonedObject = {};

    if (!Array.isArray(undesiredKeys)) {
      return original;
    }

    for (const key in original) {
      if (!undesiredKeys.includes(key)) {
        clonedObject[key] = original[key];
      }
    }

    return clonedObject;
  };

  /**
   *
   * Executes a GET request via AJAX to the URL specified and executes the callback once the action has returned.
   * Parses the information obtained as a JSON before passing it to the callback function
   *
   * @private
   * @param {string} url Location of the server where the GET action will be executed
   * @param {function} callback JavaScript function to execute if the AJAX request is succesfull.
   */
  const _ajaxRequest = function (url, callback) {
    _dispatchAdParametersStartEvent();
    const xmlHttpRequest = new XMLHttpRequest();

    xmlHttpRequest.onreadystatechange = function () {
      if (xmlHttpRequest.readyState === 4 && xmlHttpRequest.status === 200) {
        let data;

        try {
          data = JSON.parse(xmlHttpRequest.responseText);
        } catch (err) {
          return;
        }

        _dispatchAdParametersEndEvent();
        // If callback function was passed execute it, once the AJAX has returned.
        if (typeof callback === 'function' && data !== null) {
          callback(data);
        }
      }
    };

    xmlHttpRequest.open('GET', url, true);
    xmlHttpRequest.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
    xmlHttpRequest.setRequestHeader('Cache-Control', 'no-cache');
    xmlHttpRequest.setRequestHeader('Content-type', 'application/json; charset=utf-8');
    xmlHttpRequest.send();
  };

  /**
   *
   * Handles the Ad Available event for the apntag.
   * Handles the render of the ad if the available ad is native, otherwise does nothing as the AST library handles the rendering of banner ads.
   * Handles the render of the ad if the available ad is native, otherwise does nothing as the AST library handles the rendering of banner ads.
   *
   * @private
   * @param {object} response Available ad information.
   */
  const _astAdAvailableHandler = function (response) {
    const element = document.getElementById(response.targetId);

    if (element) {
      element.dataset.loaded = true;
    }

    if (
      response.adType.indexOf('native') !== -1 &&
      !!response.native &&
      !!response.native.image &&
      !!response.native.image.url
    ) {
      // clear the target element to remove the hidden template
      element.innerHTML = '';

      createNativeAd(element, response);
    }
  };

  /**
   *
   * Handles the Ad no bid event for apntag.
   * If the ad has not been bid then hide the parent div so that the space without the ad is not shown.
   * "No bid" means no offer in an ad auction, resulting in an empty advertising space.
   * We use an OnEvent to listen if the ad is noBid: true, the latter is a property that comes in the response
   * if the ad was bid the response says noBid:False.
   * @param {object} response - Ad information, if it was not bid.
   * See more details at.
   * @see {@link https://docs.xandr.com/bundle/seller-tag/page/seller-tag/on-event.html}
   * @see {@link https://docs.xandr.com/bundle/seller-tag/page/seller-tag/ad-object-api.html}
   */
  const _astAdNoBidHandler = function (response) {
    _printAdInformation('Ad no bid ');
    _printAdInformation(response);
    _dispatchAdRequestEndEvent(response);
    const element = document.getElementById(response.targetId);
    if (!element || element?.dataset.noRemove === 'true') {
      element.dataset.loaded = 'true';
      return;
    }

    const parentElement = element.closest('[data-native="true"]');
    if (parentElement) {
      parentElement.remove();
    }
  };

  /**
   *
   * Initialize the apntag object with the global page options.
   *
   * @private
   */
  const _astGlobalOptions = function () {
    apntag.setPageOpts({
      member: CONFIGURATION.get('MEMBERID'),
      disablePsa: true,
    });
  };
  /**
   *
   * Define an Ad Tag to the apntag object.
   *
   * @private
   * @param {String} divElementId div Element Id
   * @param {Number} width width
   * @param {Number} height height
   * @param {Number} index index
   */
  const _astDefineTag = function (divElementId, width, height, index) {
    let nativeLayouts;
    let allowedFormatsArray = ['banner'];
    const data = JSON.parse(parameters);
    const keywords = _omit(data, ['placements', 'useLazyLoad']);
    const element = document.getElementById(divElementId);
    const nativeAdKey = divElementId.split("--")[1];
    const isNativeSupported = element.dataset.native === 'true' || nativeAdTemplates[nativeAdKey];
    let currentPlacement = data.placements.filter((el) => {
      const elementId = divElementId.toLowerCase().replace(CONFIGURATION.get('HTMLDIVPREFIX'), '');
      return elementId.split('--')[1].toLowerCase() === el.split('|')[1].toLowerCase();
    });

    if (typeof currentPlacement === 'undefined' || currentPlacement === null || currentPlacement.length === 0) {
      return;
    }

    if (divElementId.indexOf('native') !== -1 || isNativeSupported) {
      allowedFormatsArray = ['native'];
      nativeLayouts = {
        title: { required: true, max_length: 70 },
        body: { required: true, max_length: 120 },
        image: { required: false },
        sponsoredBy: { required: true, max_length: 60 },
        icon: { required: false },
        cta: { required: false },
      };
    }

    currentPlacement = currentPlacement[0].split('|')[0];
    keywords.ref = ++index; // Ad ref parameter to Ad call.

    apntag.defineTag({
      member: CONFIGURATION.get('MEMBERID'),
      tagId: currentPlacement * 1, // Placement ID in console.
      sizes: [width, height],
      allowedFormats: allowedFormatsArray,
      targetId: divElementId,
      keywords,
      native: nativeLayouts,
      customIframeProps: {
        title: 'Advertising',
      },
    });
    apntag.showTag(divElementId);
  };

  /**
   *
   * Executes a request to App Nexus servers for new ads.
   *
   * @private
   * @param {String} divElementId div Element Id
   * @param {Number} width width
   * @param {Number} height height
   * @param {Number} index index
   * @return {Function} Function with the parameters defined for an ad placement.
   */
  const _executeRequestToAppNexus = function (divElementId, width, height, index) {
    return function () {
      _astGlobalOptions();
      _astDefineTag(divElementId, width, height, index);
    };
  };

  /**
   *
   * Search for HTML elements with the class "nhs_AstDiv" and execute a define call to AppNexus server for that position
   *
   * @private
   * @param {Boolean} overwriteAds Overwrite Ads flag
   * @param {String} selector Element class name or CSS Selector
   */
  const _createAppNexusRequest = function (overwriteAds, selector) {
    let index;
    let elementId;
    let element;
    let width;
    let height;
    const appNexusRequests = [];

    // We receive parameter named "selector" for load Ads regarding to class name sent from each View or PartialView (https://builderhomesite.atlassian.net/browse/BDXNHS-1569)
    var adElements = document.querySelectorAll(selector);
    var adElements = Array.prototype.slice.call(adElements);

    adElements = adElements.sort((a, b) => {
      let indexA = a && a.id && a.id.indexOf('native') > 1;
      let indexB = b && b.id && b.id.indexOf('native') > 1;
      const widthA = 1000 - Math.round(a.getAttribute('data-width'));
      const heightA = Math.round(a.getAttribute('data-height'));
      const widthB = 1000 - Math.round(b.getAttribute('data-width'));
      const heightB = Math.round(b.getAttribute('data-height'));

      indexA = indexA * 1000 + widthA + heightA;
      indexB = indexB * 1000 + widthB + heightB;

      return indexB - indexA;
    });

    if (!adElements || adElements.length === 0) {
      return;
    }

    let length = adElements.length;

    for (index = 0; index < length; index += 1) {
      element = adElements[index];
      elementId = element.id;
      width = Math.round(element.getAttribute('data-width'));
      height = Math.round(element.getAttribute('data-height'));
      const internalNativeAdTemplate = element.querySelector('[data-native-ad-template]');
      const isNativeSupported =
        elementId.includes('native') || element.dataset.native === 'true' || internalNativeAdTemplate;
      const nativeAdKey = elementId.split("--")[1];
      if (!nativeAdTemplates[nativeAdKey] && internalNativeAdTemplate) {
        nativeAdTemplates[nativeAdKey] = internalNativeAdTemplate;
      }

      if (width === 0 && height === 0 && elementId !== CONFIGURATION.get('SKINADID')) {
        continue;
      }

      // Ignore Ad containers that already have some content inside them, and ignore any element hidden via CSS.
      // Making an exception for Skin Ad. The offsetParent is null for elements with position "Fixed" as its the case of the Skin Ad.
      if (
        (!!element.querySelector('iFrame') && !overwriteAds) ||
        (element.offsetParent === null && elementId !== CONFIGURATION.get('SKINADID') && !isNativeSupported)
      ) {
        continue;
      }

      appNexusRequests.push(_executeRequestToAppNexus(elementId, width, height, index));
    }

    length = appNexusRequests.length;
    for (index = 0; index < length; index += 1) {
      apntag.anq.push(appNexusRequests[index]);
    }

    // All tags have been sent to AppNexus, ask AppNexus to load all the ads at once.
    apntag.loadTags();
  };

  const _astAdLoadedHandler = (event) => {
    _printAdInformation('Ad loaded ');
    _printAdInformation(event);
    _dispatchAdRequestEndEvent(event);
  };

  /**
   *
   * Indicates the AST JS Library is already loaded, and trigger the loadAdsEvent.
   */
  const _astLoadedCallback = function () {
    isAstLoaded = true;
    isAstLoading = false;
    apntag = root.apntag;

    if (!apntag) {
      return;
    }

    apntag.onEvent('adLoaded', _astAdLoadedHandler);
    apntag.onEvent('adAvailable', _astAdAvailableHandler);
    apntag.onEvent('adNoBid', _astAdNoBidHandler);
    root.dispatchEvent(loadAdsEvent);
  };

  /**
   *
   * Attachs an event to the object, checks the browser supports to choose the better option to handle the load event.
   *
   * @private
   * @param {object} object - Object where the load event will be attached.
   * @param {function} event - Function that will be executed when the event load for the object is triggered.
   */
  const _attachEventToLoad = function (object, event) {
    if (object.addEventListener) {
      object.addEventListener('load', event, false);
    } else if (root.attachEvent) {
      object.attachEvent('onload', event);
    }
  };

  /**
   *
   * Load the AST Script async.
   *
   * @private
   * @param {function} callback - Function to execute when the load of the AST.js file has finished.
   */
  const _loadAstScriptAsync = function () {
    if (isAstLoaded) {
      // AST JS Library has already been loaded. Do not load it again.
      return;
    }
    _dispatchAdsScriptStartEvent();
    const tag = document.getElementsByTagName('meta')[0];
    const script = document.createElement('script');

    _attachEventToLoad(script, () => {
      root.dispatchEvent(atsLoadedEvent);
      _dispatchAdsScriptEndEvent();
    });
    script.async = true;
    script.src = document.location.protocol + CONFIGURATION.get('ASTURL');
    script.type = 'text/javascript';
    script.dataset.resource = 'ast';
    tag.parentNode.insertBefore(script, tag);
    isAstLoading = true;
  };

  /**
   *
   * Callback function to be executed when the AJAX to retrieve the Ad Parameters has returned. Stores the data in the global parameters variable and changes the value of the areParametersDefined flag to true.
   *
   * @private
   * @callback
   * @param {object} data JSON object with the parameters.
   */
  const _adParametersLoadedCallback = function (data) {
    parameters = data;
    areParametersDefined = true;

    for (let i = 0; i < onParametersLoaded.length; i++) {
      const callback = onParametersLoaded[i];
      if (typeof callback === 'function') {
        callback();
      }
    }
    onParametersLoaded = [];
  };

  /**
   *
   * Callback function to be executed when the AJAX to retrieve the Ad Parameters and refresh the parameters.
   *
   * @private
   * @callback
   * @param {object} data JSON object with the parameters.
   */
  const _adParametersLoadedAndRefreshCallback = function (data) {
    parameters = data;
    areParametersDefined = true;
    for (var i = 0; i < onParametersLoaded.length; i++) {
      const callback = onParametersLoaded[i];
      if (typeof callback === 'function') {
        callback();
      }
    }
    onParametersLoaded = [];
    if (
      typeof apntag === 'undefined' ||
      apntag === null ||
      typeof apntag.refresh !== 'function' ||
      !areParametersDefined
    ) {
      return;
    }

    const utCalls = getUtCallGroups();
    const adIds = getNodeIdsFromSelectors(utCalls);
    const keywords = JSON.parse(parameters);
    delete keywords.placements;
    delete keywords.useLazyLoad;
    apntag.clearRequest();

    for (var i = 0; i < adIds.length; i++) {
      apntag.setKeywords(adIds[i], keywords);

      const element = document.getElementById(adIds[i]);
      if (element.dataset.override === "true"){
        fastdom.mutate(() => {
          element.innerHTML = '';
        });
      }
    }

    utCalls.forEach((utCall) => {
      _createAppNexusRequest(true, utCall);
    });
  };

  /**
   * logs event and time information if a appnexus cookie is present.
   * @param event - a string that represents an event or action that occurred
   * in the application.
   * @param time - is a variable that represents the time associated with the event being logged.
   */
  const _printInformation = (event, time) => {
    if (localStorage.getItem('debugAppNexus')) {
      console.info(`${event} time: ${time}`);
    }
  };

  /**
   * logs the provided text to the console if a specific cookie 'debugAppNexus' is present.
   * @param text - The `text` parameter is a string that contains information to be printed.
   */
  const _printAdInformation = (text) => {
    if (localStorage.getItem('debugAppNexus')) {
      console.info(text);
    }
  };

  /**
   * dispatches a custom event named 'astStart' and records the timing of the event.
   */
  const _dispatchAdsScriptStartEvent = () => {
    root.dispatchEvent(new CustomEvent('adScriptStart'));
  };

  /**
   * dispatches a custom event named 'astEnd' and prints
   * information about the time taken for the AST script to download.
   */
  const _dispatchAdsScriptEndEvent = () => {
    root.dispatchEvent(new CustomEvent('adScriptEnd'));
    _printInformation('Ads script', performance.now());
  };

  /**
   * dispatches a custom event named 'adParametersStart' and records the timing when it is called.
   */
  const _dispatchAdParametersStartEvent = () => {
    root.dispatchEvent(new CustomEvent('adParametersStart'));
  };

  /**
   * dispatches a custom event named 'adParametersEnd' and calculates the time taken for ad parameters to end.
   */
  const _dispatchAdParametersEndEvent = () => {
    root.dispatchEvent(new CustomEvent('adParametersEnd'));
    _printInformation('Ad parameters', performance.now());
  };

  /**
   * dispatches a custom event named 'adRequestStart' and records the timing of the ad request start.
   */
  const _dispatchAdRequestStartEvent = () => {
    root.dispatchEvent(new CustomEvent('adRequestStart'));
  };

  /**
   * dispatches a custom event named 'adRequestEnd' with details about the element ID and timing information.
   * @param event - object containing information related to an ad request.
   */
  const _dispatchAdRequestEndEvent = (event) => {
    root.dispatchEvent(
      new CustomEvent('adRequestEnd', {
        detail: {
          elementId: event.targetId,
        },
      })
    );
    _printInformation(`${event.targetId}`, performance.now());
  };

  /**
   *
   * Initialize and executes the process to load the Ads from AppNexus.
   *
   * @private
   */
  const _loadAds = function () {
    _dispatchAdRequestStartEvent();
    let url = CONFIGURATION.get('PARAMETERSURL') + escape(window.location.href) + '&_=' + new Date().getTime();
    const params = document.querySelectorAll('[data-ads-parameter]');
    for (let i = 0; i < params.length; i++) {
      const value = params[i].value;
      const name = params[i].dataset.paramname;
      url += `&${name}=${value}`;
    }
    const skinAd = document.getElementById(CONFIGURATION.get('SKINADID'));

    if (skinAd !== 'undefined' && skinAd !== null) {
      document.body.insertBefore(skinAd, document.body.firstChild);
    }

    // Trigger an AJAX Request to obtain the parameters we need to pass into AppNexus calls while we wait until the AST Library is ready.
    _ajaxRequest(url, _adParametersLoadedCallback);

    if (isAstLoaded) {
      // The AST JS library has been loaded we can make calls to AppNexus.
      // Trigger onLoadAdsEvent to execute the call to AppNexus and generate the Ads.
      root.dispatchEvent(loadAdsEvent);
    } else if (!isAstLoading) {
      // The AST JS library has not been loaded and there's not request to load it.
      // Trigger a request to load the AST JS library and add a request to load Ads in the adRequestsQueue.
      _loadAstScriptAsync(_astLoadedCallback);
    }
  };

  /**
   *
   * Causes a new ad server call and refreshes the specific ad element on the page.
   *
   * @public
   * @param {string} divElementId The unique identifier of a specific ad slot. One or more identifiers can be specified in an array.
   */
  const refreshAd = function (divElementId) {
    if (
      typeof apntag === 'undefined' ||
      apntag === null ||
      typeof apntag.refresh !== 'function' ||
      !areParametersDefined
    ) {
      return;
    }

    apntag.refresh([divElementId]);
  };

  /**
   *
   * Causes a new ad server call and refreshes all the ad elements on the page.
   *
   * @public
   */
  const refreshAllAds = function () {
    if (
      typeof apntag === 'undefined' ||
      apntag === null ||
      typeof apntag.refresh !== 'function' ||
      !areParametersDefined
    ) {
      return;
    }

    const utCalls = getUtCallGroups();
    utCalls.forEach((utCall) => {
      const elements = getIdsFromElements(document.querySelectorAll(utCall));
      apntag.refresh(elements);
    });
  };

  /**
   *
   * Causes a new ad server call and refreshes all the ad elements on the page.
   *
   * @public
   */
  const refreshAllAdsAndUpdateParams = function () {
    _dispatchAdRequestStartEvent();
    let url = CONFIGURATION.get('PARAMETERSURL') + escape(window.location.href) + '&_=' + new Date().getTime();
    const params = document.querySelectorAll('[data-ads-parameter]');
    for (let i = 0; i < params.length; i++) {
      const value = params[i].value;
      const name = params[i].dataset.paramname;
      url += `&${name}=${value}`;
    }
    _ajaxRequest(url, _adParametersLoadedAndRefreshCallback);
  };

  /**
   *
   * Get the element Id from Array.
   *
   * @param {String} elements Array of elements.
   */
  var getIdsFromElements = function (elements) {
    return Array.prototype.slice.call(elements).map((element) => element.id);
  };

  /**
   *
   * Delete an element from the AppNexus Call.
   *
   * @param {String} tagId Identifier of the ad impression, usually the ID of the HTML container for the Ad.
   */
  const deleteAdOnDemand = function (tagId) {
    if (apntag && apntag.requests && apntag.requests.tags) {
      delete apntag.requests.tags[tagId];
    }
  };

  /**
   *
   * Delete an element from the AppNexus Call.
   *
   * @param {String} tagId Identifier of the ad impression, usually the ID of the HTML container for the Ad.
   */
  const deleteAdsOnDemand = function (tagsId) {
    if (apntag && apntag.requests && apntag.requests.tags) {
      Array.prototype.map.call(tagsId, (tagId) => {
        delete apntag.requests.tags[tagId];
      });
    }
  };

  /**
   *
   * Simplify the Ads creation in one only call
   *
   * @param {Boolean} param Decides if ad its going to be overwritten.
   * @public
   */
  const generateAllAds = function (param) {
    param = typeof param === 'undefined' ? true : !!param;

    if (
      typeof apntag === 'undefined' ||
      apntag === null ||
      typeof apntag.refresh !== 'function' ||
      !areParametersDefined
    ) {
      return;
    }

    const utCalls = getUtCallGroups();
    utCalls.forEach((utCall) => {
      _createAppNexusRequest(param, utCall);
    });
  };

  /**
   *
   * Causes a new ad server call. This method should be used only when the elements where the ads are going to be displayed are added via JavaScript.
   *
   * @param {Boolean} overwrite overwrite
   * @public
   */
  const regenerateAdCalls = function (overwrite) {
    _dispatchAdRequestStartEvent();
    if (
      typeof apntag === 'undefined' ||
      apntag === null ||
      typeof apntag.refresh !== 'function' ||
      !areParametersDefined
    ) {
      return;
    }

    const utCalls = getUtCallGroups();
    utCalls.forEach((utCall) => {
      _createAppNexusRequest(overwrite, utCall);
    });
  };

  /**
   *
   * Creates a new ad request on demand to the given query selector expression passed as parameter.
   *
   * @param {String | HTMLElement[]} selector - query selector expression that will be used to find the ad(s) we want to create on demand.
   * @public
   */
  const createAdOnDemand = function (selector) {
    let index;
    let elementId;
    let element;
    let width;
    let height;
    const appNexusRequests = [];

    // This is to validate if the global variable is undefined and if contains the push method.
    if (typeof apntag === 'undefined' || apntag === null || typeof apntag.anq.push !== 'function') {
      return;
    }

    // If the parameters has not being defined call the function itself to wait for the AJAX to get the parameters to end.
    if (!areParametersDefined) {
      onParametersLoaded.push(() => {
        _createAppNexusRequest(false, selector);
      });
      return;
    }

    if (typeof selector !== 'string' && !Array.isArray(selector)) {
      return;
    }

    const adElements = Array.isArray(selector) ? selector : document.querySelectorAll(selector);

    if (!adElements || adElements.length === 0) {
      return;
    }

    let length = adElements.length;
    const randomNumber = Math.floor(Math.random() * 2000) + 100;

    for (index = 0; index < length; index += 1) {
      element = adElements[index];
      elementId = element.id;
      width = Math.round(element.getAttribute('data-width'));
      height = Math.round(element.getAttribute('data-height'));

      if (width === 0 && height === 0) {
        continue;
      }

      // HACK: Index parameter could be tracked in AppNexus code to identify each ad so we are using the index plus a random base to avoid collisions.
      // This code can be improved.
      appNexusRequests.push(_executeRequestToAppNexus(elementId, width, height, index + randomNumber));
    }

    length = appNexusRequests.length;

    for (index = 0; index < length; index += 1) {
      apntag.anq.push(appNexusRequests[index]);
    }

    // All tags have been sent to AppNexus, ask AppNexus to load all the ads at once.
    apntag.loadTags();
  };

  /**
   *
   * Deletes an ad tag previously requested from the AppNexus manager. This method prevents tags removed from the HTML to be requested to the ads server.
   * @param {String} selector a string containing one or more selectors to match. This string must be a valid CSS selector string.
   */
  const deleteAdsBySelector = function (selector) {
    const nativeIds = [];
    const elements = document.querySelectorAll(selector);

    if (elements && elements.length > 0) {
      Array.prototype.map.call(elements, (div) => {
        nativeIds.push(div.id);
      });
    }

    if (nativeIds.length > 0) {
      deleteAdsOnDemand(nativeIds);
    }
  };

  // Attach an event listener to the root object (window) for the custom onLoadAdsEvent event.
  loadAdsEvent.initEvent('onLoadAdsEvent', false, false);

  // Initial load with default parameters for _createAppNexusRequest, related to ticket https://builderhomesite.atlassian.net/browse/BDXNHS-1569
  root.addEventListener(
    'onLoadAdsEvent',
    () => {
      const createRequestsCallback = function () {
        const utCalls = getUtCallGroups();
        utCalls.forEach((utCall) => {
          _createAppNexusRequest(false, utCall);
        });
      };

      if (!areParametersDefined) {
        onParametersLoaded.push(createRequestsCallback);
      } else {
        createRequestsCallback();
      }
    },
    false
  );

  // Attach an event listener to the root object (window) for the custom onLoadAdsEvent event.
  atsLoadedEvent.initEvent('onAstLoadedEvent', false, false);
  root.addEventListener('onAstLoadedEvent', _astLoadedCallback, false);

  if (document.readyState === 'complete') {
    _loadAds();
  } else {
    // On the load event of the root element (window) trigger a call to load Ads.
    _attachEventToLoad(root, _loadAds);
  }

  /**
   *
   * Attaches the click event to the native ad card of the SRP
   *
   * @public
   */
  var bindEventsToNativeAdCard = function (iframeForNativeAd, nativePosition, clickTrackers, isMobileDevice) {
    const iframeBody = iframeForNativeAd.contentWindow.document.body;

    const onBeginTouch = function (e) {
      iframeBody.addEventListener('touchend', clickAdFunction);
    };

    const onMove = function (e) {
      iframeBody.removeEventListener('touchend', clickAdFunction);
    };

    var clickAdFunction = function (e) {
      e.preventDefault();
      for (let j = 0; j < clickTrackers.length; j += 1) {
        requestHttpGet(clickTrackers[j]);
      }

      const url = iframeBody.querySelector('[data-native-contact]').href;
      window.open(url, '_blank');

      return false;
    };

    iframeBody.addEventListener('click', clickAdFunction);
  };

  return {
    refreshAds: refreshAllAds,
    refreshAdsAndUpdateParams: refreshAllAdsAndUpdateParams,
    refreshAdByName: refreshAd,
    regenerateAds: regenerateAdCalls,
    generateAllAds,
    loadAdOnDemand: createAdOnDemand,
    deleteAdByTagName: deleteAdOnDemand,
    deleteAllAdsBySelector: deleteAdsBySelector,
  };
});
